import { useTranslation } from "react-i18next";
import { localizeBrand } from "../../utils/brandTerms";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useCompatibility } from "./useCompatibility";
import { ConnectionRequest } from "./types";
import { statusLabel } from "./labels";
import {
  ArrowLeft,
  Heart,
  Clock,
  CheckCircle,
  XCircle,
  MessageCircle,
  FileText,
  User,
  Mail,
  Calendar,
  Filter,
  Loader2,
  Send,
  Inbox,
  Users,
  Sparkles,
} from "lucide-react";
import { getAppLanguage, toLocale } from "../../utils/appLanguage";

type FilterType = "all" | "pending" | "accepted" | "rejected";

export const RequestHistory: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const {
    loading,
    history,
    fetchHistory,
    acceptConnectionRequest,
    rejectConnectionRequest,
  } = useCompatibility();

  const [filter, setFilter] = useState<FilterType>("all");
  const [actioningId, setActioningId] = useState<string | null>(null);

  useEffect(() => {
    fetchHistory(filter === "all" ? undefined : filter).catch(() =>
      toast.error(t("social.history.loadFailed")),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const handleAccept = async (requestId: string, otherUserName: string) => {
    setActioningId(requestId);
    try {
      const result = await acceptConnectionRequest(requestId);
      if (result.success) {
        toast.success(t("social.history.connected"));
        navigate(`/compatibility-synastry/${requestId}`, {
          state: { otherUserName },
        });
      }
    } catch (error) {
      toast.error(t("social.history.acceptFailed"));
    } finally {
      setActioningId(null);
    }
  };

  const handleReject = async (requestId: string) => {
    setActioningId(requestId);
    try {
      await rejectConnectionRequest(requestId);
      toast.success(t("social.history.declined"));
      await fetchHistory(filter === "all" ? undefined : filter);
    } catch (error) {
      toast.error(t("social.history.rejectFailed"));
    } finally {
      setActioningId(null);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "accepted":
        return <CheckCircle className="w-4 h-4" />;
      case "rejected":
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "pending":
        return "bg-amber-400/15 text-amber-300 border-amber-400/30";
      case "accepted":
        return "bg-emerald-400/15 text-emerald-300 border-emerald-400/30";
      case "rejected":
        return "bg-rose-400/15 text-rose-300 border-rose-400/30";
      default:
        return "bg-[#221a29] text-[#f7f3fa] border-white/10";
    }
  };

  const getFilterIcon = (filterValue: FilterType) => {
    switch (filterValue) {
      case "all":
        return <Users className="w-4 h-4" />;
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "accepted":
        return <CheckCircle className="w-4 h-4" />;
      case "rejected":
        return <XCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string): string => {
    return statusLabel(status);
  };

  const renderItem = (item: ConnectionRequest) => {
    const { otherUser, isSender } = item;
    const isActioning = actioningId === item._id;

    return (
      <div
        key={item._id}
        className="group bg-[#1a131f] backdrop-blur-xl rounded-3xl p-5 shadow-lg shadow-black/30 border border-white/10 hover:brightness-110 hover:shadow-black/30 transition-all duration-300 hover:scale-[1.01]"
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-14 h-14 rounded-2xl bg-[#a78bff] flex items-center justify-center text-[#0f0a14] text-xl font-bold shadow-lg shadow-black/30 group-hover:brightness-110 group-hover:shadow-black/30 transition-all duration-200">
                {otherUser.username.charAt(0).toUpperCase()}
              </div>
              {item.status === "accepted" && (
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-400 rounded-full border-2 border-white/10 flex items-center justify-center">
                  <CheckCircle className="w-3 h-3 text-white" />
                </div>
              )}
            </div>

            <div>
              <h3 className="text-base font-semibold text-[#f7f3fa] group-hover:text-rose-400 transition-colors">
                {otherUser.username}
              </h3>
              <div className="flex items-center gap-3 text-xs text-[#c8bfd1]">
                <span className="flex items-center gap-1">
                  {isSender ? (
                    <Send className="w-3 h-3" />
                  ) : (
                    <Inbox className="w-3 h-3" />
                  )}
                  {isSender ? t("social.history.sent") : t("social.history.received")}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {new Date(item.createdAt).toLocaleDateString(toLocale(getAppLanguage()), {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>

          <div
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${getStatusColor(item.status)}`}
          >
            {getStatusIcon(item.status)}
            <span className="text-xs font-semibold">
              {getStatusLabel(item.status)}
            </span>
          </div>
        </div>

        {item.message && (
          <div className="mt-3 px-4 py-3 bg-[#221a29] rounded-xl border border-white/10">
            <p className="text-sm text-[#c8bfd1] italic flex items-start gap-2">
              <span className="text-rose-400">"</span>
              {item.message}
              <span className="text-rose-400">"</span>
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-white/10">
          <span className="text-xs text-[#c8bfd1] flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {new Date(item.createdAt).toLocaleString(toLocale(getAppLanguage()), {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>

          <div className="flex flex-wrap gap-2">
            {item.status === "pending" && !isSender && (
              <>
                <button
                  type="button"
                  className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-emerald-400 to-emerald-500 text-white font-medium rounded-xl shadow-lg shadow-black/30 hover:brightness-110 hover:shadow-black/30 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleAccept(item._id, otherUser.username)}
                  disabled={isActioning}
                >
                  {isActioning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {t("social.history.accept")}
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 px-5 py-2 bg-[#221a29] text-rose-300 font-medium rounded-xl hover:bg-[#2a2133] hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => handleReject(item._id)}
                  disabled={isActioning}
                >
                  {isActioning ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  {t("social.history.decline")}
                </button>
              </>
            )}

            {item.status === "pending" && isSender && (
              <div className="flex items-center gap-2 px-4 py-2 bg-amber-400/10 rounded-xl border border-amber-400/30">
                <Clock className="w-4 h-4 text-amber-500 animate-pulse" />
                <span className="text-sm font-medium text-amber-300">
                  {t("social.history.waiting")}
                </span>
              </div>
            )}

            {item.status === "accepted" && (
              <>
                <button
                  type="button"
                  className="flex items-center gap-2 px-5 py-2 bg-[#221a29] text-[#f7f3fa] font-medium rounded-xl hover:bg-[#2a2133] hover:scale-105 transition-all duration-200"
                  onClick={() =>
                    navigate(`/compatibility-synastry/${item._id}`)
                  }
                >
                  <FileText className="w-4 h-4" />
                  {t("social.history.report")}
                </button>
                <button
                  type="button"
                  className="flex items-center gap-2 px-5 py-2 bg-[#a78bff] text-[#0f0a14] font-medium rounded-xl shadow-lg shadow-black/30 hover:brightness-110 hover:shadow-black/30 hover:scale-105 transition-all duration-200"
                  onClick={() =>
                    navigate(`/compatibility-chat/${item._id}`, {
                      state: { otherUserName: otherUser.username },
                    })
                  }
                >
                  <MessageCircle className="w-4 h-4" />
                  {t("social.history.chat")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const filters: Array<{ label: string; value: FilterType }> = [
    { label: t("social.filter.all"), value: "all" },
    { label: t("social.filter.pending"), value: "pending" },
    { label: t("social.filter.accepted"), value: "accepted" },
    { label: t("social.filter.rejected"), value: "rejected" },
  ];

  const getFilterCount = (filterValue: FilterType) => {
    if (filterValue === "all") return history.length;
    return history.filter((item) => item.status === filterValue).length;
  };

  return (
    <div className="min-h-screen bg-[#0f0a14] p-4 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-[#a78bff]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#a78bff]/10 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#a78bff]/10 rounded-full blur-3xl"></div>

      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="bg-[#1a131f] backdrop-blur-xl rounded-3xl p-6 mb-6 shadow-lg shadow-black/30 border border-white/10">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <Link
                to="/matches"
                className="p-2 hover:bg-white/10 rounded-xl transition-all duration-200 group"
              >
                <ArrowLeft className="w-5 h-5 text-[#c8bfd1] group-hover:text-rose-400 transition-colors" />
              </Link>
              <div>
                <div className="flex items-center gap-3">
                  <div className="bg-[#a78bff] p-2 rounded-xl shadow-lg shadow-black/30">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <h1 className="text-2xl font-bold text-[#f7f3fa]">
                    {t("social.matches.history")}
                  </h1>
                </div>
                <p className="text-sm text-[#c8bfd1] ml-12 mt-1">
                  {history.length > 0
                    ? t("social.history.count", { count: history.length })
                    : t("social.history.none")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#c8bfd1]">
              <Sparkles className="w-4 h-4 text-rose-400" />
              <span>{localizeBrand("Astria", i18n.language)}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-[#1a131f] backdrop-blur-xl rounded-3xl p-4 mb-6 shadow-lg shadow-black/30 border border-white/10">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 mr-2 text-[#c8bfd1]">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filter:</span>
            </div>
            {filters.map((f) => (
              <button
                key={f.value}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  filter === f.value
                    ? "bg-[#a78bff] text-[#0f0a14] shadow-lg shadow-black/30 scale-105"
                    : "bg-[#221a29]/50 text-[#c8bfd1] hover:bg-[#221a29] hover:scale-105"
                }`}
                onClick={() => setFilter(f.value)}
              >
                {getFilterIcon(f.value)}
                {f.label}
                <span
                  className={`text-xs px-2 py-0.5 rounded-full ${
                    filter === f.value
                      ? "bg-[#1a131f]/20 text-white"
                      : "bg-[#2a2133] text-[#c8bfd1]"
                  }`}
                >
                  {getFilterCount(f.value)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && history.length === 0 && (
          <div className="bg-[#1a131f] backdrop-blur-xl rounded-3xl p-16 text-center shadow-lg shadow-black/30 border border-white/10">
            <div className="flex flex-col items-center justify-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-rose-400/30 rounded-full"></div>
                <div className="absolute top-0 left-0 w-16 h-16 border-4 border-rose-400 rounded-full border-t-transparent animate-spin"></div>
              </div>
              <span className="mt-4 text-[#c8bfd1] font-medium">
                {t("social.history.loading")}
              </span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && history.length === 0 && (
          <div className="bg-[#1a131f] backdrop-blur-xl rounded-3xl p-16 text-center shadow-lg shadow-black/30 border border-white/10">
            <div className="w-24 h-24 bg-[#a78bff]/15 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-12 h-12 text-rose-400" />
            </div>
            <h3 className="text-2xl font-bold text-[#f7f3fa] mb-3">
              {t("social.history.emptyTitle")}
            </h3>
            <p className="text-[#c8bfd1] max-w-md mx-auto leading-relaxed">
              {t("social.history.emptyBody")}
            </p>
            <Link
              to="/matches"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#a78bff] text-[#0f0a14] font-medium rounded-xl shadow-lg shadow-black/30 hover:brightness-110 hover:shadow-black/30 hover:scale-105 transition-all duration-200"
            >
              <Users className="w-5 h-5" />
              {t("social.history.find")}
            </Link>
          </div>
        )}

        {/* History List */}
        <div className="space-y-4">
          {history.map((item) => renderItem(item))}
        </div>

        {/* Footer */}
        {history.length > 0 && (
          <div className="text-center mt-8">
            <p className="text-xs text-[#c8bfd1] flex items-center justify-center gap-2">
              <Heart className="w-3 h-3 text-rose-400" />
              <span>{t("social.history.footer")}</span>
              <Heart className="w-3 h-3 text-rose-400" />
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestHistory;
